// 「UnLock ボタンを押した」記録のストレージ（2026-08-06 追加）。
//
// ## なぜ保存が要るのか
//
// このゲームの解放状態は**保存せず記録から導出する**のが方針
// （[game/progress.ts](../game/progress.ts) の注記）。だが「**押した／押していない**」は
// どんな記録からも導出できない。ここだけが例外で、保存する唯一の解放関連の状態。
//
// ## どこへ保存するか
//
// **設定スライス**（`/api/settings` の `games['triangle-sweeper']`）へ相乗りする。
// サーバーは `games.*` の中身を解釈しないので **D1 のスキーマ変更が要らない**
// （進捗の `player_stage_progress` に足すと列かフォーマットを触ることになる）。
// 値は `SettingValue = number | boolean | string` しか置けないので、**昇順 CSV の文字列**
// にした。D1 を直接覗いたときに `"3,4,5"` と読めるほうが調べやすい。
//
// ## 壊れ方の見積り
//
// 最悪でも**演出がもう一度出るだけ**。資格（ポイント）は記録から導出されるので、
// 解錠の記録を失っても★も進捗も減らないし、UnLock を押し直せば元通りになる。
// そのうえで取りこぼしを減らすため、サーバー由来の値は**和集合で取り込む**（`applyClaimed`）＝
// 端末Aの解錠が、まだ同期していない端末Bの push で消えることがない。
import { INITIAL_UNLOCKED_SHAPES } from '../config/gameConfig';
import { SHAPE_COUNT } from '../game/shapes';
// ⚠️ **このモジュールは初期バンドルに載る**（registry → gameSettings → ここ、が静的な鎖）。
//    Phaser も [progress.ts](progress.ts) も引かないこと。記録は引数で受け取る。
import { isShapePlayed, unlockedShapes } from '../game/progress';
import type { StageRecord } from '@/platform/stageRecord';

/** localStorage のキー。**接頭辞は `game_key` と同じ文字列**（データ削除がこれで掃く）。 */
const CLAIMED_KEY = 'triangle-sweeper.claimedShapes';

/**
 * 済ませた後始末の版（[repairUnplayedClaims](#repairUnplayedClaims) 用）。
 * **1度だけ走らせたい処理**なので、走ったことを保存する。番号を上げれば作り直せる。
 */
const REPAIR_KEY = 'triangle-sweeper.claimedRepair';
const REPAIR_VERSION = '1';

/** 設定ペイロード内のキー（サーバーの `games['triangle-sweeper'].claimedShapes`）。 */
export const CLAIMED_SETTING_KEY = 'claimedShapes';

/**
 * CSV → 集合。**範囲外・重複・非数値は捨てる**（手で書き換えられた値や、
 * 形状を減らしたあとの古い保存を読んでも落ちないように）。
 * 先頭 `INITIAL_UNLOCKED_SHAPES` は無条件解錠なので保存対象外＝読み込みでも落とす。
 */
function parse(csv: string | null | undefined): Set<number> {
  const out = new Set<number>();
  if (!csv) return out;
  for (const part of csv.split(',')) {
    const n = Number(part.trim());
    if (!Number.isInteger(n)) continue;
    if (n < INITIAL_UNLOCKED_SHAPES || n >= SHAPE_COUNT) continue;
    out.add(n);
  }
  return out;
}

/** 集合 → 昇順 CSV。 */
function serialize(set: ReadonlySet<number>): string {
  return [...set].sort((a, b) => a - b).join(',');
}

function readRaw(): string | null {
  try {
    return localStorage.getItem(CLAIMED_KEY);
  } catch {
    return null;
  }
}

/** localStorage へ書くだけ（サーバーへは送らない）。サーバー由来の取り込みで使う。 */
function writeLocal(set: ReadonlySet<number>): void {
  try {
    localStorage.setItem(CLAIMED_KEY, serialize(set));
  } catch {
    /* 保存失敗は無視（プライベートモード等）。演出がもう一度出るだけ */
  }
}

/**
 * 保存して**サーバーへも送る**（登録プレイヤーのみ・`pushSettingsToServer` が内部で判定）。
 * [progress.ts](progress.ts) の `save()` が毎回 `pushToServer()` を呼ぶのと同じ形で、
 * ★や自己ベストと**同じタイミングで**プレイヤーの記録として残る。
 *
 * ⚠️ **動的 import にしてある**。`platform/net/settings` は `games/registry` を、
 * registry は `gameSettings` を、`gameSettings` はこのファイルを import するので、
 * 静的に書くと循環参照になる（今の初期化順なら動くが、順序に依存した壊れ方をする）。
 * registry 自身が進捗の reconcile を動的 import しているのと同じ逃がし方。
 */
function persist(set: ReadonlySet<number>): void {
  writeLocal(set);
  void import('@/platform/net/settings')
    .then((m) => m.pushSettingsToServer())
    .catch(() => {
      /* オフライン時は無視。次に設定を触ったときか、別の解錠で再送される */
    });
}

/** 解錠済みの形状（先頭3形状は含まない＝`isClaimed` が無条件で真を返す）。 */
export function getClaimedShapes(): Set<number> {
  return parse(readRaw());
}

/**
 * 1形状を解錠済みにする。**押した瞬間に呼ぶ**（演出の完了を待たない）＝
 * 途中で画面を閉じても「開けたのに閉じている」状態にならない。
 */
export function claimShape(shapeIndex: number): void {
  if (shapeIndex < INITIAL_UNLOCKED_SHAPES || shapeIndex >= SHAPE_COUNT) return;
  const set = getClaimedShapes();
  if (set.has(shapeIndex)) return;
  set.add(shapeIndex);
  persist(set);
}

/**
 * 解錠記録を今の記録と突き合わせる。**3段**（順番に意味がある）:
 *
 * 1. **引き継ぎ**（保存がまだ無い既存プレイヤー） … `seedClaimed`
 * 2. **未プレイぶんの取り消し**（1度だけ） … `repairUnplayedClaims`
 * 3. **資格を失ったぶんの取り消し**（毎回） … `pruneStaleClaims`
 *
 * 🔴 **呼ぶのは「そのセッションでまだ1回も遊んでいない時点」だけ**（2026-08-06 に本番で踏んだ）。
 * 1 は「更新前から資格があった形状」と「たった今クリアして資格が立った形状」を
 * **区別できない**（見えるのは今の記録だけ）。形状選択画面を開いた時点で呼ぶと、
 * ちょうど解放されたばかりの形状まで解錠済みにしてしまい、
 * **UnLock ボタンが出ないまま自動で開いたように見える**。
 * 呼び出し口は [runtime.ts](../runtime.ts) の `createGame`（＝シーンが動く前）と、
 * [registry.ts](../../registry.ts) の進捗 reconcile 直後（＝サーバー進捗が届いた直後）の2つ。
 *
 * ⚠️ **記録が空のときは何もしない**（3段すべて）。登録プレイヤーが新しい端末で開いた直後は
 * サーバーからの進捗がまだ届いていない。そこで「資格ゼロ」を確定させると、1 なら
 * 本来要らない演出が後から出るし、2・3 なら**正当な解錠を消してしまう**。
 * 記録が空＝資格は先頭3形状だけ＝いじる中身も無いので、何もしないことによる実害はない
 * （届いたあとに reconcile 側の呼び出しが拾う）。
 */
export function syncClaimedShapes(records: readonly StageRecord[]): void {
  if (!records.some((r) => r.clears > 0)) return; // まだ何もクリアしていない＝記録が届いていない
  const qualified = unlockedShapes(records);

  if (seedClaimed(qualified)) return; // 引き継ぎ直後は 2・3 が必ず空振りする

  const claimed = getClaimedShapes();
  const before = claimed.size;
  repairUnplayedClaims(records, claimed);
  pruneStaleClaims(qualified, claimed);
  // 変化が無いなら書かない（毎回サーバーへ push するのを避ける）。
  if (claimed.size !== before) persist(claimed);
}

/**
 * **既存プレイヤーの引き継ぎ**（この機能を入れる前から遊んでいる人）。
 * 保存がまだ無いなら、いま資格のある形状を**すべて解錠済みとして書き込む**
 * ＝更新した瞬間に UnLock ボタンが大量に並ぶのを避ける（2026-08-06・ユーザー選択）。
 * 書き込んだら `true`（＝以降の後始末は不要）。
 */
function seedClaimed(qualified: readonly boolean[]): boolean {
  if (readRaw() !== null) return false; // 初期化済み
  const seeded = new Set<number>();
  qualified.forEach((on, i) => {
    if (on && i >= INITIAL_UNLOCKED_SHAPES) seeded.add(i);
  });
  // 引き継ぎぶんも**サーバーへ送る**。送らないと、2台目の端末で同じ形状の
  // UnLock ボタンがもう一度並ぶ（サーバーに何も無い＝未解錠と読めてしまう）。
  persist(seeded);
  markRepaired(); // 引き継ぎ直後の集合は今の条件どおり＝後始末済みと同じ状態
  return true;
}

/**
 * 🔴 **旧条件でばら撒かれた解錠の後始末**（2026-08-07・**1度だけ**）。
 *
 * 上の引き継ぎ（`seedClaimed`）は「**そのときの条件で**資格のある形状」を全部書き込む。
 * 2026-08-06 はこれが**旧ポイント制**（1形状 6pt・閾値 `1×形状番号`）の下で走ったため、
 * 初期3形状を Hard まで解いていた端末では **形状 3〜14 が丸ごと解錠済み**になった。
 * その日のうちに条件を **★6個ごと**へ厳しくしたので資格は縮んだが、
 * 解錠記録のほうは残る＝**再び資格を得ても UnLock ボタンが出ない**（本番 iPad で発覚）。
 *
 * そこで **1度も遊んでいない形状の解錠を取り消す**。「実際に遊べていた形状」と
 * 「引き継ぎで解錠済みにされただけの形状」を分ける手がかりは**クリア記録しかない**
 * （押した時刻などは保存していない）。遊んだ形状にボタンを出し直しても嬉しくないので、
 * そちらは触らない。
 *
 * ⚠️ **1度きりでなければならない**。毎回走らせると「UnLock を押したがまだ遊んでいない形状」の
 * 解錠まで取り消され、次の起動でボタンが**もう一度出る**。
 */
function repairUnplayedClaims(records: readonly StageRecord[], claimed: Set<number>): void {
  if (readRepairVersion() === REPAIR_VERSION) return;
  for (const i of [...claimed]) {
    if (!isShapePlayed(records, i)) claimed.delete(i);
  }
  markRepaired();
}

/**
 * 🔴 **資格を失った形状の解錠を取り消す**（2026-08-07・**毎回**）。
 *
 * 解錠は増える一方の集合だが、**資格（★）の条件は後から変わりうる**。
 * 資格を失った形状は遊べない＝解錠が効いていないので、記録として残しておく意味が無く、
 * 残すと**再び資格を得たときに UnLock ボタンを飛ばしてしまう**。
 *
 * ★は自己ベスト（`applyClear` が `Math.max`）なので、**条件が同じである限りこれは発動しない**
 * ＝通常のプレイでは何も起きず、条件を厳しくした回にだけ効く安全弁。
 */
function pruneStaleClaims(qualified: readonly boolean[], claimed: Set<number>): void {
  for (const i of [...claimed]) {
    if (!qualified[i]) claimed.delete(i);
  }
}

function readRepairVersion(): string | null {
  try {
    return localStorage.getItem(REPAIR_KEY);
  } catch {
    return null;
  }
}

function markRepaired(): void {
  try {
    localStorage.setItem(REPAIR_KEY, REPAIR_VERSION);
  } catch {
    /* 保存失敗は無視（次の起動でもう一度後始末が走るだけ） */
  }
}

// ------------------------------------------------------------------ 設定スライス
/** サーバーへ送る値（未初期化なら空文字）。 */
export function readClaimed(): string {
  return readRaw() ?? '';
}

/**
 * サーバー由来の値を取り込む。**置換ではなく和集合**にするのが要点＝
 * 別の端末で解錠したぶんを、こちらのまだ古い値で消してしまわない
 * （解錠は増える一方の集合なので、和集合が意味的にも正しい）。
 */
export function applyClaimed(csv: string): void {
  const merged = getClaimedShapes();
  for (const i of parse(csv)) merged.add(i);
  // ⚠️ ここは `persist` にしない。サーバー由来の値を受け取った直後に送り返すことになる。
  writeLocal(merged);
}

/** 既定値へ戻す（PREFERENCE の「初期設定に戻す」）。 */
export function resetClaimedShapes(): void {
  try {
    localStorage.removeItem(CLAIMED_KEY);
    // 後始末の版も落とす（次の起動は「まっさらな端末」と同じ道を通る＝引き継ぎから）。
    localStorage.removeItem(REPAIR_KEY);
  } catch {
    /* 失敗は無視 */
  }
}
